import React, { useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { styled } from 'baseui'
import { MOBILE_MEDIA_QUERY } from '@/constants/responsive'
import { findEffectivePlacementWithFallback, PlacementType } from './findEffectivePlacement'

// Bound the measured surface before placement clamps it. A surface wider or
// taller than the viewport cannot be fixed by moving its anchor alone.
const PopoverSurface = styled('div', ({ $maxHeight }: { $maxHeight?: React.CSSProperties['maxHeight'] }) => {
  const maxHeight = typeof $maxHeight === 'number' ? `${$maxHeight}px` : $maxHeight
  return {
    maxHeight,
    [MOBILE_MEDIA_QUERY]: {
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: maxHeight && maxHeight !== 'none'
        ? `min(${maxHeight}, calc(100dvh - 16px))`
        : 'calc(100dvh - 16px)',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
  }
})

interface PopoverPlacementProps {
  placement?: PlacementType
  margin?: number
  buttonRef: React.RefObject<HTMLElement>
  relativeToRef?: React.RefObject<HTMLElement>
  /** Vertical fallback anchor when neither side of `relativeToRef` fits. */
  fallbackAnchorRef?: React.RefObject<HTMLElement>
  /**
   * Centers the cross axis on this element instead of the anchor — e.g. placed
   * `right` of a whole panel (`relativeToRef`) but vertically centered on the
   * row inside it that triggered the popover. Applies to `left`/`right`/`top`/`bottom`.
   */
  alignToRef?: React.RefObject<HTMLElement>
  /**
   * Change it to re-measure without remounting when the anchor refs now point at
   * a different element (e.g. hover moved to a sibling). A remount would reset
   * the position and hide the popover for a frame.
   */
  repositionKey?: string | number
  children?: React.ReactNode
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
}

function PopoverPlacement({
  placement = 'bottom',
  margin = 8,
  buttonRef,
  relativeToRef,
  fallbackAnchorRef,
  alignToRef,
  repositionKey,
  children,
  style,
  onClick,
  onMouseDown,
}: PopoverPlacementProps) {
  const { maxHeight, ...surfaceStyle } = style ?? {}
  const popoverRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const rafIdRef = useRef<number | null>(null)

  // Layout effect so the first measurement lands before paint — no hidden frame.
  useLayoutEffect(() => {
    if (!buttonRef?.current || !popoverRef.current) return

    const updatePosition = () => {
      if (!buttonRef.current || !popoverRef.current) return

      const anchorElement = relativeToRef?.current || buttonRef.current
      const anchorElementRect = anchorElement.getBoundingClientRect()
      const popoverRect = popoverRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Skip if popover hasn't rendered with actual dimensions yet
      if (popoverRect.height === 0 || popoverRect.width === 0) {
        return
      }

      // Skip if button has invalid dimensions (e.g., during drag when element is hidden/moved)
      if (anchorElementRect.width === 0 || anchorElementRect.height === 0) {
        return
      }

      // Skip if button is completely outside viewport (likely being dragged)
      if (anchorElementRect.right < 0 || anchorElementRect.bottom < 0 || 
          anchorElementRect.left > viewportWidth || anchorElementRect.top > viewportHeight) {
        return
      }

      let top = 0
      let left = 0

      // Determine effective placement with automatic flipping
      const { placement: effectivePlacement, anchorRect } = findEffectivePlacementWithFallback(placement, {
        buttonRect: anchorElementRect,
        popoverRect,
        margin,
        viewportWidth,
        viewportHeight,
      }, fallbackAnchorRef?.current?.getBoundingClientRect())

      // Calculate position based on effective placement
      switch (effectivePlacement) {
        case 'top':
          top = anchorRect.top - popoverRect.height - margin
          left = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2
          break
        case 'bottom':
          top = anchorRect.bottom + margin
          left = anchorRect.left + anchorRect.width / 2 - popoverRect.width / 2
          break
        case 'left':
          top = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2
          left = anchorRect.left - popoverRect.width - margin
          break
        case 'right':
          top = anchorRect.top + anchorRect.height / 2 - popoverRect.height / 2
          left = anchorRect.right + margin
          break
        case 'topLeft':
          top = anchorRect.top - popoverRect.height - margin
          left = anchorRect.left
          break
        case 'topRight':
          top = anchorRect.top - popoverRect.height - margin
          left = anchorRect.right - popoverRect.width
          break
        case 'bottomLeft':
          top = anchorRect.bottom + margin
          left = anchorRect.left
          break
        case 'bottomRight':
          top = anchorRect.bottom + margin
          left = anchorRect.right - popoverRect.width
          break
      }

      const alignRect = alignToRef?.current?.getBoundingClientRect()
      if (alignRect) {
        if (effectivePlacement === 'left' || effectivePlacement === 'right') {
          top = alignRect.top + alignRect.height / 2 - popoverRect.height / 2
        } else if (effectivePlacement === 'top' || effectivePlacement === 'bottom') {
          left = alignRect.left + alignRect.width / 2 - popoverRect.width / 2
        }
      }

      // Additional boundary clamping to ensure popover stays within viewport
      // Clamp horizontal position
      if (left < 0) {
        left = margin
      } else if (left + popoverRect.width > viewportWidth) {
        left = viewportWidth - popoverRect.width - margin
      }

      // Clamp vertical position
      if (top < 0) {
        top = margin
      } else if (top + popoverRect.height > viewportHeight) {
        top = viewportHeight - popoverRect.height - margin
      }

      setPosition({ top: Math.round(top), left: Math.round(left) })
    }

    const updatePositionWithRAF = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      rafIdRef.current = requestAnimationFrame(updatePosition)
    }

    updatePosition()
    // Retry shortly after in case content (e.g. images) was not laid out yet
    const initialTimer = setTimeout(updatePosition, 0)
    // Second update after content has time to render
    const secondTimer = setTimeout(updatePosition, 50)

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePositionWithRAF, true)
    window.addEventListener('resize', updatePositionWithRAF)

    // Use ResizeObserver to detect when popover content size changes
    const resizeObserver = new ResizeObserver(updatePositionWithRAF)
    resizeObserver.observe(popoverRef.current)

    // Use MutationObserver to detect layout changes
    const mutationObserver = new MutationObserver(updatePositionWithRAF)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    return () => {
      clearTimeout(initialTimer)
      clearTimeout(secondTimer)
      window.removeEventListener('scroll', updatePositionWithRAF, true)
      window.removeEventListener('resize', updatePositionWithRAF)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [buttonRef, relativeToRef, fallbackAnchorRef, alignToRef, repositionKey, placement, margin])

  return createPortal(
    <PopoverSurface
      ref={popoverRef}
      $maxHeight={maxHeight}
      data-floating-popover="true"
      style={{
        position: 'fixed',
        top: `${position?.top}px`,
        left: `${position?.left}px`,
        visibility: position ? 'visible' : 'hidden',
        ...surfaceStyle,
      }}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      {children}
    </PopoverSurface>,
    document.body
  )
}

export default PopoverPlacement
